import type {
  DivisionsTree,
  GlobalSearchUserResult,
  ISchedule,
  OfficeWithFloors
} from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { useAuthenticated } from "@gettactic/hooks";
import { UserAvatar } from "@v2/components/display/UserAvatar/UserAvatar";
import { useCallback } from "react";
import { GlobalSearchListItemLayout } from "../GlobalSearchListItemLayout";
import { OfficeFloorInfo } from "../OfficeFloorInfo";
import { getOfficeAndFloor } from "../globalSearchOfficeUtils";
import {
  usePersonScheduleInfo
} from "../hooks/useScheduleInfo";
import { FavoriteFilledIcon } from "@v2/icons/FavoriteFilledIcon";

interface PersonItemProps {
  person: GlobalSearchUserResult;
  schedules?: ISchedule[];
  isActive: boolean;
  onSelect: () => void;
  divisionCategories?: {
    offices: Record<string, OfficeWithFloors>;
    tree: DivisionsTree | null;
  };
}

export function PersonItem({
  person,
  schedules = [],
  isActive,
  onSelect,
  divisionCategories
}: PersonItemProps) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const getOfficeFloorInfo = useCallback(
    (officeId?: string | null, parentId?: string | null) => {
      const { officeName, floorName } = getOfficeAndFloor({
        officeId,
        parentId,
        divisionCategories,
        userOfficesById: authenticatedUser?.offices?.byId
      });
      return { officeName, floorName };
    },
    [divisionCategories, authenticatedUser]
  );

  const scheduleInfo = usePersonScheduleInfo(
    schedules,
    getOfficeFloorInfo,
  );

  return (
    <GlobalSearchListItemLayout
      isActive={isActive}
      className={cn("gap-4 p-2")}
      onClick={onSelect}
    >
      <UserAvatar
        user={{
          name: person.name,
          profile_photo_url: person.profile_photo_url
        }}
        size="md"
        className="text-xs"
      />

      <div className="flex min-w-0 flex-1 flex-col gap-1.8 overflow-hidden">
        <div className="flex items-center justify-between gap-2">
          <h4 className="flex min-w-0 items-center gap-1 truncate font-semibold text-gray-900 text-sm">
            {(person.favorite_team_ids?.length ?? 0) > 0 ? (
                <FavoriteFilledIcon className={"size-3.5 text-icon-paleGray/20"} />
              ) : (
                null
              )}
            {person.name}
          </h4>
          {scheduleInfo?.resourceName ? (
            <span className="flex-shrink-0 rounded border border-gray-300 bg-transparent px-1.5 py-0.5 text-gray-700 text-xs">
              {scheduleInfo.resourceName}
            </span>
          ) : (
            <span className="flex-shrink-0 rounded border border-gray-300 bg-transparent px-1.5 py-0.5 text-gray-400 text-xs">
              Not in office
            </span>
          )}
        </div>
        {!!scheduleInfo && (
          <OfficeFloorInfo
            officeName={scheduleInfo.officeName}
            floorName={scheduleInfo.floorName}
          />
        )}
      </div>
    </GlobalSearchListItemLayout>
  );
}
